import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useFund } from "@/contexts/FundContext";
import API from "@/lib/api";
import {
  Users, FileText, ArrowLeftRight, BarChart3, ArrowRight,
} from "lucide-react";

export default function PortalDashboard() {
  const { fundId } = useParams();
  const { activeFund, fundLoading } = useFund();
  const [investorCount, setInvestorCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const basePath = `/super-admin/funds/${fundId}/portal`;

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await API.get(`/investors?fund_id=${fundId}`);
        setInvestorCount(Array.isArray(data) ? data.length : 0);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [fundId]);

  const modules = [
    {
      title: "Investors",
      description: "Manage investor onboarding and profiles",
      icon: Users,
      color: "text-blue-400",
      bg: "bg-blue-500/10 border-blue-500/20",
      iconBg: "bg-blue-500/15",
      path: `${basePath}/investors`,
      stat: `${investorCount} investors`,
    },
    {
      title: "Documents",
      description: "Upload and manage fund documents",
      icon: FileText,
      color: "text-emerald-400",
      bg: "bg-emerald-500/10 border-emerald-500/20",
      iconBg: "bg-emerald-500/15",
      path: `${basePath}/documents`,
      stat: null,
    },
    {
      title: "Transactions",
      description: "Capital and performance transactions",
      icon: ArrowLeftRight,
      color: "text-amber-400",
      bg: "bg-amber-500/10 border-amber-500/20",
      iconBg: "bg-amber-500/15",
      path: `${basePath}/transactions`,
      stat: null,
    },
    {
      title: "Reporting",
      description: "Generate statements and analytics",
      icon: BarChart3,
      color: "text-violet-400",
      bg: "bg-violet-500/10 border-violet-500/20",
      iconBg: "bg-violet-500/15",
      path: `${basePath}/reporting`,
      stat: null,
    },
  ];

  const fundName = activeFund?.name || "Fund";

  return (
    <div className="space-y-8 animate-fade-in" data-testid="portal-dashboard">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="font-heading text-3xl font-bold tracking-tight">Tychi Portal</h1>
          {activeFund && (
            <Badge variant="secondary" className="text-xs">
              {fundName}
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground text-sm">
          Fund administration portal for {fundLoading ? "..." : fundName}
        </p>
      </div>

      {/* Fund Quick Info */}
      {activeFund && (
        <Card className="bg-primary/5 border-primary/10">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Fund Type</p>
                  <p className="text-sm font-medium mt-0.5">{activeFund.fund_type || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Status</p>
                  <Badge variant="outline" className="mt-0.5 text-[10px] uppercase tracking-wider">
                    {activeFund.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Reporting</p>
                  <p className="text-sm font-medium mt-0.5 capitalize">{activeFund.reporting_frequency || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Currency</p>
                  <p className="text-sm font-medium mt-0.5">{activeFund.currency || "USD"}</p>
                </div>
              </div>
              <Button asChild variant="secondary" size="sm" className="border border-border text-xs">
                <Link to={`/super-admin/funds/${fundId}`}>Fund Settings</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Module Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {modules.map((mod) => (
          <Link key={mod.title} to={mod.path} data-testid={`portal-module-${mod.title.toLowerCase()}`}>
            <Card className={`${mod.bg} hover:shadow-[0_0_25px_rgba(108,71,196,0.08)] transition-all duration-200 hover:scale-[1.01] cursor-pointer h-full`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg ${mod.iconBg} flex items-center justify-center`}>
                    <mod.icon className={`w-5 h-5 ${mod.color}`} strokeWidth={1.5} />
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
                <h3 className="font-heading font-semibold text-base mb-1">{mod.title}</h3>
                <p className="text-xs text-muted-foreground">{mod.description}</p>
                {mod.stat && !loading && (
                  <p className="text-xs font-medium text-foreground/70 mt-2">{mod.stat}</p>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
